import { Injectable, signal } from '@angular/core';

export interface DebugTab {
    readonly id: string;
    readonly label: string;
    readonly getData: () => unknown;
}

export interface DebugAction {
    readonly id: string;
    readonly label: string;
    readonly icon?: string;
    readonly run: () => void;
}

@Injectable({
    providedIn: 'root',
})
export class DebugPanelService {
    readonly open = signal(false);
    readonly activeTabId = signal<string | null>(null);
    readonly copied = signal(false);

    private readonly builtInTabs = signal<DebugTab[]>([]);
    private readonly featureTabs = signal<DebugTab[]>([]);

    readonly tabs = signal<DebugTab[]>([]);

    private recomputeTabs(): void {
        this.tabs.set([...this.builtInTabs(), ...this.featureTabs()]);
        if (!this.activeTabId() && this.tabs().length > 0) {
            this.activeTabId.set(this.tabs()[0].id);
        }
    }

    registerBuiltInTab(tab: DebugTab): void {
        this.builtInTabs.update((tabs) => [...tabs.filter((t) => t.id !== tab.id), tab]);
        this.recomputeTabs();
    }

    /** Feature pages register their own tabs (e.g. form drafts, api payloads). Returns an unregister function to call on destroy. */
    registerTab(tab: DebugTab): () => void {
        this.featureTabs.update((tabs) => [...tabs, tab]);
        this.recomputeTabs();
        this.activeTabId.set(tab.id);

        return () => {
            this.featureTabs.update((tabs) => tabs.filter((t) => t.id !== tab.id));
            this.recomputeTabs();
            if (this.activeTabId() === tab.id) {
                this.activeTabId.set(this.tabs()[0]?.id ?? null);
            }
        };
    }

    readonly actions = signal<DebugAction[]>([]);

    /** Feature pages register their own action buttons (e.g. reload report, force resync). Returns an unregister function to call on destroy. */
    registerAction(action: DebugAction): () => void {
        this.actions.update((actions) => [...actions, action]);

        return () => {
            this.actions.update((actions) => actions.filter((a) => a.id !== action.id));
        };
    }

    getActiveTabData(): unknown {
        return (
            this.tabs()
                .find((tab) => tab.id === this.activeTabId())
                ?.getData() ?? null
        );
    }

    copyActiveTabToClipboard(): void {
        const data = this.getActiveTabData();
        navigator.clipboard.writeText(JSON.stringify(data, null, 2)).then(() => {
            this.copied.set(true);
            setTimeout(() => this.copied.set(false), 1500);
        });
    }
}
